import {
  focusBlockEnd,
  getSelectedModelsCommand,
} from '@blocksuite/affine-shared/commands';
import { isInsideBlockByFlavour } from '@blocksuite/affine-shared/utils';
import type {
  SlashMenuActionContext,
  SlashMenuActionItem,
  SlashMenuConfig,
  SlashMenuContext,
} from '@blocksuite/affine-widget-slash-menu';
import { TableIcon } from '@blocksuite/icons/lit';

import {
  insertRichTableBlockCommand,
  MAX_COLUMNS,
  MAX_ROWS,
} from '../commands/insert-rich-table.js';

const DEFAULT_COLUMNS = 3;
const DEFAULT_ROWS = 3;

/** Offered while the argument is still empty; the first row is the default. */
const SIZE_PRESETS: [number, number][] = [
  [DEFAULT_COLUMNS, DEFAULT_ROWS],
  [2, 2],
  [3, 5],
  [4, 4],
];

export type RichTableSize = { columns: number; rows: number };

/**
 * Parses `3x4`, `3×4` or `3*4` into a size. Also accepts a bare `3`, read as
 * a square. Returns null when the argument is not a size at all, or is out of
 * range — the caller then falls back to the default.
 */
export function parseRichTableSize(argument: string): RichTableSize | null {
  const match = /^\s*(\d{1,2})\s*(?:[x×*]\s*(\d{1,2})\s*)?$/i.exec(argument);
  if (!match) return null;

  const columns = Number(match[1]);
  const rows = match[2] === undefined ? columns : Number(match[2]);
  if (!Number.isInteger(columns) || !Number.isInteger(rows)) return null;
  if (columns < 1 || columns > MAX_COLUMNS) return null;
  if (rows < 1 || rows > MAX_ROWS) return null;

  return { columns, rows };
}

function insertRichTable(ctx: SlashMenuContext, size: RichTableSize) {
  const { std } = ctx;
  std.command
    .chain()
    .pipe(getSelectedModelsCommand)
    .pipe(insertRichTableBlockCommand, {
      columnCount: size.columns,
      rowCount: size.rows,
      place: 'after',
      removeEmptyLine: true,
    })
    .pipe(({ insertedRichTableBlockId }) => {
      if (!insertedRichTableBlockId) return;
      // The blocks exist in the store immediately but their components do
      // not, so the caret has to wait for the host to render.
      std.host.updateComplete
        .then(() => {
          const table = std.store.getModelById(insertedRichTableBlockId);
          const firstParagraphId =
            table?.children[0]?.children[0]?.children[0]?.id;
          if (!firstParagraphId) return;
          const focusBlock = std.view.getBlock(firstParagraphId);
          if (!focusBlock) return;
          std.command.exec(focusBlockEnd, { focusBlock });
        })
        .catch(console.error);
    })
    .run();
}

function createSizeItem(size: RichTableSize): SlashMenuActionItem {
  return {
    name: `${size.columns} × ${size.rows} table`,
    description: `${size.columns} columns, ${size.rows} rows`,
    icon: TableIcon(),
    group: '4_Content & Media@20',
    action: ctx => insertRichTable(ctx, size),
  };
}

function getSizeArgumentItems(
  _ctx: SlashMenuContext,
  actionContext: SlashMenuActionContext
): SlashMenuActionItem[] {
  const argument = actionContext.argument.trim();
  if (!argument)
    return SIZE_PRESETS.map(([columns, rows]) =>
      createSizeItem({ columns, rows })
    );

  const parsed = parseRichTableSize(argument);
  // An unparseable argument returns nothing, which keeps the parent item
  // visible so Enter still inserts the default size.
  return parsed ? [createSizeItem(parsed)] : [];
}

export const richTableSlashMenuConfig: SlashMenuConfig = {
  items: [
    {
      name: 'Rich table',
      description: 'Table whose cells take lists, images and slash commands.',
      icon: TableIcon(),
      group: '4_Content & Media@19',
      searchAlias: ['rich-table', 'richtable', 'grid', 'block table'],
      // A space closes the menu for items without this, so `/rich-table 4x6`
      // would otherwise be impossible to type.
      acceptsArgument: true,
      when: ({ model }) =>
        !isInsideBlockByFlavour(model.store, model, 'affine:rich-table') &&
        !isInsideBlockByFlavour(model.store, model, 'affine:edgeless-text'),
      getArgumentItems: getSizeArgumentItems,
      action: (ctx, actionContext) =>
        insertRichTable(
          ctx,
          parseRichTableSize(actionContext?.argument ?? '') ?? {
            columns: DEFAULT_COLUMNS,
            rows: DEFAULT_ROWS,
          }
        ),
    },
  ],
};
